import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Modal } from '../../components/ui/Modal'
import { Field } from '../../components/ui/Field'
import { Input, Textarea, Select } from '../../components/ui/Input'
import { Button } from '../../components/ui/Button'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { PermissionGate } from '../../components/PermissionGate'
import { roadmapApi } from '../../api/roadmap.api'
import { tasksApi } from '../../api/tasks.api'
import { ApiError } from '../../api/client'
import { toast } from '../../store/toastStore'
import { AssigneePicker } from '../tasks/shared/AssigneePicker'
import { ProgressSlider } from '../tasks/shared/ProgressSlider'
import { ProgressReadonlyPill } from '../tasks/shared/ProgressReadonlyPill'
import type { RoadmapEntry, RoadmapStatus } from '../../api/types'
import formStyles from '../../components/ui/Form.module.css'

const STATUS_OPTIONS: { value: RoadmapStatus; label: string }[] = [
  { value: 'PLANNED', label: 'Geplant' },
  { value: 'IN_PROGRESS', label: 'In Arbeit' },
  { value: 'COMPLETED', label: 'Abgeschlossen' },
  { value: 'DELAYED', label: 'Verzögert' },
]

export function RoadmapEntryFormModal({ projectId, entry, onClose }: { projectId: string; entry?: RoadmapEntry | null; onClose: () => void }) {
  const isEdit = Boolean(entry)
  const queryClient = useQueryClient()

  const [title, setTitle] = useState(entry?.title ?? '')
  const [phaseLabel, setPhaseLabel] = useState(entry?.phaseLabel ?? '')
  const [goal, setGoal] = useState(entry?.goal ?? '')
  const [description, setDescription] = useState(entry?.description ?? '')
  const [status, setStatus] = useState<RoadmapStatus>(entry?.status ?? 'PLANNED')
  const [startDate, setStartDate] = useState(entry?.startDate?.slice(0, 10) ?? '')
  const [targetDate, setTargetDate] = useState(entry?.targetDate?.slice(0, 10) ?? '')
  const [progress, setProgress] = useState(entry?.progress ?? 0)
  const [taskIds, setTaskIds] = useState<string[]>(entry?.tasks.map((t) => t.taskId) ?? [])
  const [userIds, setUserIds] = useState<string[]>(entry?.assignees.map((a) => a.userId) ?? [])
  const [roleIds, setRoleIds] = useState<string[]>(entry?.roles.map((r) => r.roleId) ?? [])
  const [error, setError] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const tasksQuery = useQuery({
    queryKey: ['tasks', { projectId, topLevelOnly: false }],
    queryFn: async () => (await tasksApi.list({ projectId, pageSize: 500 })).data,
  })

  const hasLinkedTasks = taskIds.length > 0
  const linkedTaskProgress = hasLinkedTasks
    ? Math.round(taskIds.reduce((sum, id) => sum + (tasksQuery.data?.find((t) => t.id === id)?.progress ?? 0), 0) / taskIds.length)
    : progress

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (isEdit && entry) {
        await roadmapApi.update(entry.id, {
          title,
          phaseLabel,
          goal,
          description,
          status,
          startDate: startDate || null,
          targetDate: targetDate || null,
          progress: hasLinkedTasks ? undefined : progress,
        })
        await roadmapApi.setTasks(entry.id, taskIds)
        await roadmapApi.setAssignees(entry.id, userIds, roleIds)
      } else {
        const res = await roadmapApi.create({ projectId, title, phaseLabel, goal, description, status, startDate: startDate || undefined, targetDate: targetDate || undefined })
        await roadmapApi.setTasks(res.data.id, taskIds)
        await roadmapApi.setAssignees(res.data.id, userIds, roleIds)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roadmap'], exact: false })
      toast.success(isEdit ? 'Roadmap-Eintrag aktualisiert.' : 'Roadmap-Eintrag angelegt.')
      onClose()
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Aktion fehlgeschlagen.'),
  })

  const deleteMutation = useMutation({
    mutationFn: () => roadmapApi.remove(entry!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roadmap'], exact: false })
      toast.success('Roadmap-Eintrag gelöscht.')
      onClose()
    },
  })

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    saveMutation.mutate()
  }

  return (
    <Modal
      title={isEdit ? 'Roadmap-Eintrag bearbeiten' : 'Neuer Roadmap-Eintrag'}
      onClose={onClose}
      size="lg"
      footer={
        <>
          {isEdit ? (
            <PermissionGate anyOf={['roadmap.manage']}>
              <Button variant="danger" onClick={() => setConfirmDelete(true)}>
                Löschen
              </Button>
            </PermissionGate>
          ) : null}
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={onSubmit} loading={saveMutation.isPending}>
            Speichern
          </Button>
        </>
      }
    >
      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        {error ? <div style={{ color: 'var(--danger)', fontSize: 'var(--text-sm)' }}>{error}</div> : null}

        <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
          <div style={{ flex: 1 }}>
            <Field label="Phase" htmlFor="rm-phase" hint='z.B. "Phase 1"'>
              <Input id="rm-phase" value={phaseLabel} onChange={(e) => setPhaseLabel(e.target.value)} required />
            </Field>
          </div>
          <div style={{ flex: 2 }}>
            <Field label="Titel" htmlFor="rm-title">
              <Input id="rm-title" value={title} onChange={(e) => setTitle(e.target.value)} required />
            </Field>
          </div>
        </div>

        <Field label="Ziel (optional)" htmlFor="rm-goal">
          <Input id="rm-goal" value={goal} onChange={(e) => setGoal(e.target.value)} />
        </Field>

        <Field label="Beschreibung (optional)" htmlFor="rm-description">
          <Textarea id="rm-description" value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>

        <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
          <div style={{ flex: 1 }}>
            <Field label="Status" htmlFor="rm-status">
              <Select id="rm-status" value={status} onChange={(e) => setStatus(e.target.value as RoadmapStatus)}>
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <div style={{ flex: 1 }}>
            <Field label="Start" htmlFor="rm-start">
              <Input id="rm-start" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </Field>
          </div>
          <div style={{ flex: 1 }}>
            <Field label="Ziel-Datum" htmlFor="rm-target">
              <Input id="rm-target" type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)} />
            </Field>
          </div>
        </div>

        <Field label="Fortschritt" htmlFor="rm-progress" hint={hasLinkedTasks ? 'Ergibt sich aus den verknüpften Aufgaben — dort erledigen statt hier eintragen.' : undefined}>
          {hasLinkedTasks ? <ProgressReadonlyPill value={linkedTaskProgress} /> : <ProgressSlider id="rm-progress" value={progress} onCommit={setProgress} />}
        </Field>

        <Field label="Verantwortliche">
          <AssigneePicker userIds={userIds} roleIds={roleIds} onToggleUser={(id) => setUserIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]))} onToggleRole={(id) => setRoleIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]))} />
        </Field>

        <Field label="Verknüpfte Aufgaben">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)', maxHeight: 140, overflowY: 'auto' }}>
            {tasksQuery.data?.map((task) => (
              <label key={task.id} className={formStyles.checkboxRow} style={{ border: '1px solid var(--surface-border)', borderRadius: 'var(--radius-md)', padding: '4px 10px' }}>
                <input
                  type="checkbox"
                  checked={taskIds.includes(task.id)}
                  onChange={() => setTaskIds((p) => (p.includes(task.id) ? p.filter((x) => x !== task.id) : [...p, task.id]))}
                />
                {task.title}
              </label>
            ))}
            {tasksQuery.data?.length === 0 ? <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>Keine Aufgaben im Projekt.</span> : null}
          </div>
        </Field>
      </form>

      {confirmDelete ? (
        <ConfirmDialog
          title="Roadmap-Eintrag löschen"
          message={`"${entry?.title}" wirklich löschen?`}
          danger
          loading={deleteMutation.isPending}
          onConfirm={() => deleteMutation.mutate()}
          onCancel={() => setConfirmDelete(false)}
        />
      ) : null}
    </Modal>
  )
}
